import { api } from './api';
import { getCurrentUser } from './authService';

export const attendanceService = {
  getAll: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        if (params[key] && params[key] !== 'all' && params[key] !== '') {
          queryParams.append(key, params[key]);
        }
      });

      const queryString = queryParams.toString();
      const url = `/api/attendance/attendance/${queryString ? `?${queryString}` : ''}`;

      const response = await api.get(url);
      return Array.isArray(response) ? response : (response.results || []);
    } catch (error) {
      ('Error fetching attendance records:', error);
      throw error;
    }
  },

  getById: async (id) => {
    try {
      const response = await api.get(`/api/attendance/attendance/${id}/`);
      return response;
    } catch (error) {
      (`Error fetching attendance record ${id}:`, error);
      throw error;
    }
  },

  create: async (data) => {
    try {
      const payload = {
        employee: data.employee,
        date: data.date,
        check_in: data.check_in || null,
        check_out: data.check_out || null,
        status: data.status || 'Present',
        latitude: data.latitude || null,
        longitude: data.longitude || null,
        location_accuracy: data.location_accuracy || null,
        notes: data.notes || ''
      };

      const response = await api.post('/api/attendance/attendance/', payload);
      return response;
    } catch (error) {
      ('Error creating attendance record:', error);
      throw error;
    }
  },

  update: async (id, data) => {
    try {
      const payload = {};
      if (data.check_in !== undefined) payload.check_in = data.check_in;
      if (data.check_out !== undefined) payload.check_out = data.check_out;
      if (data.status !== undefined) payload.status = data.status;
      if (data.notes !== undefined) payload.notes = data.notes;
      if (data.latitude !== undefined) payload.latitude = data.latitude;
      if (data.longitude !== undefined) payload.longitude = data.longitude;

      const response = await api.patch(`/api/attendance/attendance/${id}/`, payload);
      return response;
    } catch (error) {
      (`Error updating attendance record ${id}:`, error);
      throw error;
    }
  },

  delete: async (id) => {
    try {
      await api.delete(`/api/attendance/attendance/${id}/`);
      return true;
    } catch (error) {
      (`Error deleting attendance record ${id}:`, error);
      throw error;
    }
  },

  getStatistics: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        if (params[key] && params[key] !== 'all' && params[key] !== '') {
          queryParams.append(key, params[key]);
        }
      });

      const queryString = queryParams.toString();
      const url = `/api/attendance/monthly-summary/${queryString ? `?${queryString}` : ''}`;
      const response = await api.get(url);
      return Array.isArray(response) ? response : (response.results || []);
    } catch (error) {
      ('Error fetching attendance statistics:', error);
      throw error;
    }
  },

  getMyStatistics: async (params = {}) => {
    try {
      const currentUser = getCurrentUser();
      if (!currentUser) {
        throw new Error('No user logged in');
      }

      const employeeId = currentUser.employee_id || currentUser.id;
      if (!employeeId) {
        throw new Error('Employee ID not found for current user');
      }

      return attendanceService.getStatistics({ ...params, employee: employeeId });
    } catch (error) {
      ('Error fetching my attendance statistics:', error);
      throw error;
    }
  },

  getMyAttendance: async (params = {}) => {
    try {
      const currentUser = getCurrentUser();
      if (!currentUser) {
        throw new Error('No user logged in');
      }

      const employeeId = currentUser.employee_id || currentUser.id;
      if (!employeeId) {
        throw new Error('Employee ID not found for current user');
      }

      return attendanceService.getAll({ ...params, employee: employeeId });
    } catch (error) {
      ('Error fetching my attendance:', error);
      throw error;
    }
  },

  getEmployeeSummary: async (employeeId, params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        if (params[key]) {
          queryParams.append(key, params[key]);
        }
      });

      const queryString = queryParams.toString();
      const url = `/api/attendance/employees/${employeeId}/attendance_summary/${queryString ? `?${queryString}` : ''}`;

      const response = await api.get(url);
      return response;
    } catch (error) {
      (`Error fetching attendance summary for employee ${employeeId}:`, error);
      throw error;
    }
  },

  // ✅ NEW: Get complete employee attendance data (for employee profile)
  getEmployeeAttendanceData: async (employeeId, params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        if (params[key]) {
          queryParams.append(key, params[key]);
        }
      });

      const queryString = queryParams.toString();
      const url = `/api/attendance/employees/${employeeId}/attendance-data/${queryString ? `?${queryString}` : ''}`;

      const response = await api.get(url);

      return response;
    } catch (error) {
      (`Error fetching attendance data for employee ${employeeId}:`, error);
      throw error;
    }
  },

  exportCSV: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        if (params[key] && params[key] !== 'all' && params[key] !== '') {
          queryParams.append(key, params[key]);
        }
      });

      const queryString = queryParams.toString();
      const url = `/api/attendance/attendance/export_csv/${queryString ? `?${queryString}` : ''}`;

      const response = await api.get(url);
      return response;
    } catch (error) {
      ('Error exporting attendance CSV:', error);
      throw error;
    }
  },

  bulkMark: async (data) => {
    try {
      const response = await api.post('/api/attendance/attendance/bulk_mark/', data);
      return response;
    } catch (error) {
      ('Error bulk marking attendance:', error);
      throw error;
    }
  },

  verifyFace: async (formData) => {
    try {
      const response = await api.post('/api/attendance/live-attendance/capture/', formData);
      return response;
    } catch (error) {
      ('Error verifying face:', error);
      throw error;
    }
  },

  uploadFaceImages: async (formData) => {
    try {
      const response = await api.post('/api/attendance/face-photos/', formData);
      return response;
    } catch (error) {
      ('Error uploading face images:', error);
      throw error;
    }
  },

  uploadSingleFaceImage: async (formData) => {
    try {
      const response = await api.post('/api/attendance/face-photos/', formData);
      return response;
    } catch (error) {
      ('Error uploading single face image:', error);
      throw error;
    }
  },

  deleteFaceImage: async (imageId) => {
    try {
      await api.delete(`/api/attendance/face-photos/${imageId}/`);
      return true;
    } catch (error) {
      ('Error deleting face image:', error);
      throw error;
    }
  },

  getFaceImages: async (employeeId) => {
    try {
      const response = await api.get(`/api/attendance/face-photos/?employee=${employeeId}`);
      return Array.isArray(response) ? response : (response.results || []);
    } catch (error) {
      ('Error fetching face images:', error);
      throw error;
    }
  },

  isAdminOrHR: function () {
    const currentUser = getCurrentUser();
    if (!currentUser) return false;

    const role = currentUser.role || currentUser.user_type || currentUser.type || '';
    const roleLower = role.toLowerCase();
    return roleLower === 'admin' || roleLower === 'hr' || roleLower === 'hr_manager';
  },

  getCurrentEmployeeId: function () {
    const currentUser = getCurrentUser();
    if (!currentUser) return null;
    return currentUser.employee_id || currentUser.id || null;
  },

  // src/services/attendanceService.js

  transformRecord: function (record) {
    if (!record) return null;

    // Handle both attendance records and recent_records format
    return {
      id: record.id,
      employeeId: record.employeeId ?? record.employee?.employee_id ?? record.employee_id ?? record.employee ?? '',
      employeeName: record.employeeName ?? record.employee?.name ?? record.employee_name ?? 'Unknown',
      department: record.department ?? record.employee?.department ?? 'General',
      date: record.date,
      // ✅ FIX: Properly map check_in and check_out fields
      checkIn: record.checkIn ?? record.check_in ?? record.checkIn ?? '',
      checkOut: record.checkOut ?? record.check_out ?? record.checkOut ?? '',
      totalHours: record.totalHours ?? record.total_hours ?? '0',
      status: record.status || 'Absent',
      overtime: record.overtime ?? 0,
      notes: record.notes || '',
      latitude: record.latitude ?? null,
      longitude: record.longitude ?? null,
      location_accuracy: record.location_accuracy ?? null,
      // Monthly summary fields
      total_days_present: record.total_days_present || 0,
      total_days_absent: record.total_days_absent || 0,
      total_days_late: record.total_days_late || 0,
      total_days_leave: record.total_days_leave || 0,
      total_working_days: record.total_working_days || 0,
      total_hours_worked: record.total_hours_worked || 0,
      total_overtime_hours: record.total_overtime_hours || 0,
      total_late_minutes: record.total_late_minutes || 0,
      attendance_percentage: record.attendance_percentage || 0,
      average_hours_per_day: record.average_hours_per_day || 0,
    };
  },

  transformRecords: function (records) {
    if (!Array.isArray(records)) return [];
    return records.map(record => attendanceService.transformRecord(record));
  },

  toBackendFormat: function (data) {
    const backendData = {};

    if (data.checkIn !== undefined) backendData.check_in = data.checkIn;
    if (data.checkOut !== undefined) backendData.check_out = data.checkOut;
    if (data.status !== undefined) backendData.status = data.status;
    if (data.notes !== undefined) backendData.notes = data.notes;
    if (data.date !== undefined) backendData.date = data.date;
    if (data.employeeId !== undefined) backendData.employee = data.employeeId;
    if (data.totalHours !== undefined) backendData.total_hours = data.totalHours;
    if (data.latitude !== undefined) backendData.latitude = data.latitude;
    if (data.longitude !== undefined) backendData.longitude = data.longitude;
    if (data.location_accuracy !== undefined) backendData.location_accuracy = data.location_accuracy;

    return backendData;
  }
};


// Export individual functions for convenience
export const {
  getAll: getAttendanceRecords,
  getById: getAttendanceRecordById,
  create: createAttendanceRecord,
  update: updateAttendanceRecord,
  delete: deleteAttendanceRecord,
  getStatistics: getAttendanceStatistics,
  exportCSV: exportAttendanceCSV,
  bulkMark: bulkMarkAttendance,
  getEmployeeSummary: getEmployeeAttendanceSummary,
  getEmployeeAttendanceData: getEmployeeAttendanceData, // ✅ NEW
  getMyAttendance: getMyAttendanceRecords,
  getMyStatistics: getMyAttendanceStatistics,
  isAdminOrHR: checkIsAdminOrHR,
  getCurrentEmployeeId: getCurrentEmployeeId,
  transformRecord: transformAttendanceRecord,
  transformRecords: transformAttendanceRecords,
  toBackendFormat: attendanceToBackend,
  verifyFace: verifyFaceAttendance,
  uploadFaceImages: uploadEmployeeFaceImages,
  uploadSingleFaceImage: uploadSingleFaceImage,
  deleteFaceImage: deleteEmployeeFaceImage,
  getFaceImages: getEmployeeFaceImages
} = attendanceService;

export default attendanceService;